import { useState } from 'react';
import { userAppContext } from '../../context/Context';
import PricePlanesAcademic from '../Pricings/PricePlanesAcademic';
import AdvancedPlanOnly from '../Pricings/AdvancedPlanOnly';
import Swal from 'sweetalert2';
import { useAcademicApplication } from '../../hooks/useAcademicApplication';
import AcademicApplicationModal from '../Academic/AcademicApplicationModal';
import RequestPricingService from '../../services/RequestPricingService';

const UpgradePlan = () => {
  const { user, groups = [] } = userAppContext();
  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademic = groups?.includes('ROLE_ACADEMIC');
  const isStandard = groups?.includes('ROLE_STANDARD');

  const [showModal, setShowModal] = useState(false);
  const [showModalAcademic, setShowModalAcademic] = useState(false);
  const [showModalBusiness, setShowModalBusiness] = useState(false);

  // Use custom hook for academic application logic
  const {
    trialType,
    academicStatus,
    loadingStatus,
    submitAcademicApplication,
    canSeePrices: canSeePricesFromHook,
    isCommercial: isCommercialFromHook,
  } = useAcademicApplication(user?.email);

  // Use tag-based logic from hook
  const isCommercialList = isCommercialFromHook();
  const canSeePricing = canSeePricesFromHook();

  const handleUpgradeClick = () => setShowModal(true);

  const handleCloseModal = () => {
    setShowModal(false);
    setShowModalAcademic(false);
    setShowModalBusiness(false);
  };

  const handleApplyToAcademicClick = () => setShowModalAcademic(true);

  const handleSubmit = e => {
    e.preventDefault();
    // No longer need to show logout modal - Stripe handles the upgrade flow
    setShowModal(false);
  };

  const handleBusinessSubmit = async e => {
    e.preventDefault();
    setShowModalBusiness(false);

    try {
      const response = await RequestPricingService.requestPricingFromProfile(
        user?.email || '',
        user?.['custom:name'] || 'Unknown',
        user?.['custom:company'] || 'Unknown',
        user?.['custom:application_role'] || 'Unknown'
      );

      if (response.success) {
        Swal.fire({
          title: 'Success!',
          text: response.message,
          customClass: {
            confirmButton: 'btn-gradient-pink',
          },
        });
      } else {
        console.error('Error in pricing request:', response.error);
        Swal.fire('Oops! Something went wrong.');
      }
    } catch (e) {
      console.error(e);
      Swal.fire('Error with your request. Try again later.');
    }
    setShowModalAcademic(false);
  };

  let buttonsToRender = null;

  if (loadingStatus) {
    buttonsToRender = <div>Loading...</div>;
  } else if (isTrial && trialType === 'academic') {
    if (academicStatus === 'pending') {
      buttonsToRender = (
        <div>
          <p className="text-muted">Your academic application is pending approval</p>
        </div>
      );
    } else if (academicStatus === 'approved') {
      buttonsToRender = (
        <div>
          <p className="text-muted">Your academic application has been approved</p>
        </div>
      );
    } else {
      buttonsToRender = (
        <div>
          <div className="text-right">
            <button className="btn-gradient-blue" onClick={handleApplyToAcademicClick}>
              Apply for Free Academic Plan
            </button>
          </div>
        </div>
      );
    }
  } else if (isTrial && trialType === 'commercial') {
    buttonsToRender = (
      <div>
        <button className="btn-gradient-blue">
          <a href="/book-a-demo?utm_source=web" target="_blank" className="text-white" rel="noreferrer">
            Schedule a demo
          </a>
        </button>
        <button className="btn-gradient-pink" onClick={() => setShowModalBusiness(true)}>
          Request Pricing
        </button>
      </div>
    );
  } else if (isTrial && !isCommercialList) {
    if (academicStatus === 'pending') {
      buttonsToRender = (
        <div>
          <p className="text-muted">Your academic application is pending approval</p>
          <button className="btn-gradient-pink" onClick={handleUpgradeClick}>
            Upgrade your Plan
          </button>
        </div>
      );
    } else if (academicStatus === 'approved') {
      buttonsToRender = (
        <div>
          <button className="btn-gradient-blue" onClick={handleApplyToAcademicClick}>
            Pay for Academic Plan
          </button>
          <button className="btn-gradient-pink" onClick={handleUpgradeClick}>
            Upgrade your Plan
          </button>
        </div>
      );
    } else {
      buttonsToRender = (
        <div>
          <button className="btn-gradient-blue" onClick={handleApplyToAcademicClick}>
            Apply for Free Academic Plan
          </button>
          <button className="btn-gradient-pink" onClick={handleUpgradeClick}>
            Upgrade your Plan
          </button>
        </div>
      );
    }
  } else if (isTrial && isCommercialList) {
    buttonsToRender = (
      <div>
        <button className="btn-gradient-blue">
          <a href="/book-a-demo?utm_source=web" target="_blank" className="text-white" rel="noreferrer">
            Schedule a demo
          </a>
        </button>
        <button className="btn-gradient-pink" onClick={handleBusinessSubmit}>
          Request Pricing
        </button>
      </div>
    );
  } else if (!isTrial && (isAcademic || isStandard)) {
    if (canSeePricing) {
      buttonsToRender = (
        <div>
          <button className="btn-gradient-pink" onClick={handleUpgradeClick}>
            Upgrade your Plan
          </button>
        </div>
      );
    } else {
      // User cannot see pricing - different behavior for academic vs commercial
      if (isAcademic) {
        // Academic users get contact button
        buttonsToRender = (
          <div className="grid justify-items-end">
            <button className="btn-gradient-pink mr-0">
              <a href="/Support" className="text-white">
                Contact Us
              </a>
            </button>
          </div>
        );
      } else {
        // Commercial users get request pricing
        buttonsToRender = (
          <div className="grid justify-items-end">
            <p className="mb-0">Do you want to upgrade your plan?</p>
            <button className="btn-gradient-pink mr-0" onClick={() => setShowModalBusiness(true)}>
              Request Pricing
            </button>
          </div>
        );
      }
    }
  } else {
    // Fallback - no condition matched
    console.warn('No button condition matched. User state:', {
      isTrial,
      isAcademic,
      isStandard,
      trialType,
      academicStatus,
    });
  }

  return (
    <div>
      {buttonsToRender}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="popup-upgrade">
            <button className="modal-close-btn" onClick={handleCloseModal}>
              x
            </button>

            <h3 className="text-center">Upgrade Your Plan</h3>

            {!canSeePricing ? (
              <div className="text-center p-4">
                <p className="mb-4">Contact us for pricing.</p>
                <button className="btn-gradient-pink">
                  <a href="/Support" className="text-white">
                    Contact us
                  </a>
                </button>
              </div>
            ) : isStandard ? (
              <AdvancedPlanOnly />
            ) : (
              <>
                <PricePlanesAcademic />
                <p className="text-muted text-center mt-3" style={{ fontSize: '0.85em' }}>
                  Not academic?{' '}
                  <a href="/Support" style={{ color: '#667eea', textDecoration: 'underline' }}>
                    Contact us
                  </a>
                  .
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <AcademicApplicationModal
        isOpen={showModalAcademic}
        onClose={() => setShowModalAcademic(false)}
        onSubmit={submitAcademicApplication}
        academicStatus={academicStatus}
      />

      {/* Modal Business */}
      {showModalBusiness && (
        <div className="modal-overlay">
          <div className="RegisterPopup">
            <div className="apply-popup-container">
              <button className="modal-close-btn" onClick={handleCloseModal}>
                x
              </button>
              <>
                <h4 className="m-2">Are you sure you want to send a request for pricing?</h4>
                <button
                  onClick={handleBusinessSubmit}
                  className="btn-gradient-blue"
                  aria-label="submit-modal"
                >
                  Send Request
                </button>
                <button onClick={handleCloseModal} className="btn-gradient-pink" aria-label="cancel-modal">
                  Cancel
                </button>
              </>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradePlan;
